import { useCallback, useEffect, useState } from "react";
import type { UpdateBadgeStatus } from "@mypos/ui";

// PWA updater: surfaces service-worker waiting state through the same
// UpdateBadge component the Till uses. The mechanics differ — there is no
// download progress here because the SW already prefetched the precache —
// so we collapse the timeline into:
//
//   idle    → no waiting worker (or SW not registered yet)
//   available → a new worker is installed and waiting; click to activate
//   downloading → registration.update() is running a fresh fetch
//   downloaded  → same as available; visually the same CTA
//
// On activate (skipWaiting) we force a one-shot reload so the new precache
// boots cleanly. Without the reload the page stays on the old assets even
// though the SW has been swapped, which produces hard-to-debug mixed-version
// renders.

const initial: UpdateBadgeStatus = {
  event: "idle",
  version: null,
  releaseNotes: null,
  releaseDate: null,
  percent: 0,
  bytesPerSecond: 0,
  errorMessage: null,
  checkedAt: null,
};

function hasServiceWorker(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator;
}

export function useUpdater() {
  const [status, setStatus] = useState<UpdateBadgeStatus>(initial);

  useEffect(() => {
    if (!hasServiceWorker()) return;
    let cancelled = false;

    const wire = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg || cancelled) return;

        if (reg.waiting) {
          setStatus((s) => ({ ...s, event: "available" }));
        }

        const onUpdateFound = () => {
          const next = reg.installing;
          if (!next) return;
          setStatus((s) => ({ ...s, event: "downloading" }));
          next.addEventListener("statechange", () => {
            if (next.state === "installed" && navigator.serviceWorker.controller) {
              // Old SW still controls the page → new one is waiting for activation.
              setStatus((s) => ({ ...s, event: "available" }));
            } else if (next.state === "redundant") {
              setStatus((s) => ({ ...s, event: "error", errorMessage: "Install failed" }));
            }
          });
        };
        reg.addEventListener("updatefound", onUpdateFound);

        // When the SW controller swaps under our feet, reload once so the
        // user lands on the new precache. The reload-guard prevents an
        // infinite loop if controllerchange fires more than once.
        let reloading = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (reloading) return;
          reloading = true;
          window.location.reload();
        });
      } catch {
        // No SW registered (dev without preview): stay idle.
      }
    };

    void wire();
    return () => {
      cancelled = true;
    };
  }, []);

  const check = useCallback(async () => {
    if (!hasServiceWorker()) return;
    setStatus((s) => ({ ...s, event: "checking", checkedAt: Date.now() }));
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        setStatus((s) => ({ ...s, event: "idle" }));
        return;
      }
      await reg.update();
      // updatefound (above) will flip us to downloading/available; if nothing
      // is new the registration silently does nothing — flip back to "none".
      setStatus((s) => (s.event === "checking" ? { ...s, event: "none" } : s));
    } catch (err) {
      setStatus((s) => ({
        ...s,
        event: "error",
        errorMessage: String((err as Error)?.message ?? err),
      }));
    }
  }, []);

  // For the PWA there's no separate "download" step distinct from install —
  // both buttons collapse onto skipWaiting + reload.
  const activate = useCallback(async () => {
    if (!hasServiceWorker()) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg?.waiting) {
      // Nothing waiting — try a fresh check first.
      await check();
      return;
    }
    reg.waiting.postMessage({ type: "SKIP_WAITING" });
    // controllerchange handler above will trigger the reload.
  }, [check]);

  return {
    status,
    check,
    download: activate,
    install: activate,
    available: hasServiceWorker(),
  };
}
